import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";
import { profileService, type UserProfile } from "@/services/profileService";
import { adminService } from "@/services/adminService";
import { runPostLoginSync } from "@/services/syncService";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: UserProfile | null;
  profileLoading: boolean;
  isAdmin: boolean;
  cachedAdminStatus: boolean;
  adminLoading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  refreshProfile: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [cachedAdminStatus, setCachedAdminStatus] = useState<boolean>(false);
  const [adminLoading, setAdminLoading] = useState<boolean>(true);

  // Unified callback to fetch/refresh the user profile
  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setIsAdmin(false);
      setCachedAdminStatus(false);
      setProfileLoading(false);
      setAdminLoading(false);
      return;
    }

    setProfileLoading(true);
    setAdminLoading(true);
    
    try {
      const uId = user.id;
      const uEmail = user.email || "";
      const uName = user.user_metadata?.name || user.user_metadata?.full_name || uEmail.split("@")[0];

      console.log("[AuthContext] Fetching user profile for:", uEmail);
      const userProfile = await profileService.fetchOrCreateProfile(uId, uEmail, uName);
      
      setProfile(userProfile);
      
      const isUserAdmin = await adminService.isCurrentUserAdmin(user, userProfile);
      setIsAdmin(isUserAdmin);
      setCachedAdminStatus(isUserAdmin);
      
      if (isUserAdmin || userProfile) {
        // Cache admin status in localStorage to prevent flickering on reload
        localStorage.setItem(`tef_admin_status_${uId}`, JSON.stringify({
          isAdmin: isUserAdmin,
          email: uEmail,
          checkedAt: new Date().toISOString()
        }));
      }
    } catch (err) {
      console.error("[AuthContext] Failed to load or sync user profile:", err);
    } finally {
      setProfileLoading(false);
      setAdminLoading(false);
    }
  }, [user]);

  // Legacy wrapper to maintain compatibility with legacy components
  const refreshAdminStatus = useCallback(async () => {
    await refreshProfile();
  }, [refreshProfile]);

  // Trigger profile loading when user changes
  useEffect(() => {
    if (loading) return;
    
    // Optimistic fast-path using cache to prevent flicker
    if (user?.id) {
      const cacheKey = `tef_admin_status_${user.id}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const now = new Date().getTime();
          const checkedAt = new Date(parsed.checkedAt).getTime();
          // Cache validity of 24h
          if (parsed.isAdmin && now - checkedAt < 24 * 60 * 60 * 1000) {
            setCachedAdminStatus(true);
            setIsAdmin(true);
          }
        } catch (e) {
          // ignore
        }
      }

      // Run post-login data sync in background (fire-and-forget)
      // Migrates old localStorage data + flushes pending study sessions to Supabase
      runPostLoginSync(user.id).catch((err) =>
        console.warn("[AuthContext] runPostLoginSync failed:", err)
      );
    }

    refreshProfile();
  }, [user, loading, refreshProfile]);

  // Listen to profile updates from other services (e.g. Test completion increments)
  useEffect(() => {
    const handleProfileUpdate = () => {
      console.log("[AuthContext] Real-time profile update triggered");
      refreshProfile();
    };

    window.addEventListener("userProfileUpdated", handleProfileUpdate);
    return () => {
      window.removeEventListener("userProfileUpdated", handleProfileUpdate);
    };
  }, [refreshProfile]);

  // Subscribe to core Supabase auth state transitions
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      setProfileLoading(false);
      setAdminLoading(false);
      return;
    }

    // Get current session on load
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      } catch (err) {
        console.error("[AuthContext] Error getting initial session:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (!currentSession) {
        setProfile(null);
        setIsAdmin(false);
        setCachedAdminStatus(false);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signUp = async (email: string, password: string) => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }
    if (user?.id) {
      localStorage.removeItem(`tef_admin_status_${user.id}`);
    }
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } finally {
      setProfile(null);
      setIsAdmin(false);
      setCachedAdminStatus(false);
      setAdminLoading(false);
      setUser(null);
      setSession(null);
      window.location.href = "/auth";
    }
  };

  const resetPassword = async (email: string) => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?type=recovery`,
    });
    if (error) throw error;
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        profile,
        profileLoading,
        isAdmin,
        cachedAdminStatus,
        adminLoading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        refreshProfile,
        refreshAdminStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
